import type { FC } from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import styled from "styled-components";
import type { IHelpsiteArticle, IHelpsiteArticles } from "@types-contentful";
import { colours, device, richTextHelper } from "@helpers";
import { helpsiteTrackLink } from "@helpers/helpsite-mixpanel";
import Icon from "@components/Icon";

// Desktop: column, gap 20px, align-items center
// Mobile: column, gap 5px, align-items center
const FaqList = styled.div`
	display: flex;
	flex-direction: column;
	align-items: stretch;
	gap: 5px;
	width: 100%;
	box-sizing: border-box;

	@media (min-width: ${device.laptop}) {
		align-items: center;
		gap: 20px;
	}
`;

// Desktop: row layout, full width, white background with 50% opacity, border, padding 15px 10px, icon on left
// Mobile: row layout, max-width 355px, white background, dashed bottom border, padding 10px 0px 10px 10px, chevron on right
const FaqLink = styled(Link)`
	display: flex;
	flex-direction: row;
	align-items: center;
	gap: 11px;
	padding: 10px 0px 10px 10px;
	background-color: ${colours.WHITE};
	border-bottom: 1px dashed rgba(0, 0, 0, 0.4);
	border-radius: 0;
	text-decoration: none;
	color: inherit;
	width: 100%;
	justify-content: space-between;
	box-sizing: border-box;

	@media (min-width: ${device.laptop}) {
		max-width: 100%;
		background-color: rgba(255, 255, 255, 0.5);
		border: 1px solid rgba(0, 0, 0, 0.15);
		border-bottom: 1px solid rgba(0, 0, 0, 0.15);
		border-radius: 10px;
		padding: 15px 10px;
		justify-content: center;
	}
`;

const FaqContentWrapper = styled.div`
	display: flex;
	flex-direction: column;
	gap: 4px;
	flex: 1;
`;

const FaqTitle = styled.h3`
	font-family: var(--euclid-medium);
	font-size: 16px;
	font-weight: 500;
	line-height: 1.2680000066757202em;
	color: ${colours.MIDNIGHT_BLUE};
	margin: 0;
	text-align: left;

	@media (min-width: ${device.laptop}) {
		font-size: 18px;
		line-height: 1.2679999669392903em;
		text-align: left;
	}
`;

// biome-ignore lint/correctness/noUnusedVariables: used when desktop FAQ subcopy block is re-enabled
const FaqSubcopy = styled.div`
	display: none;
	font-family: var(--euclid-regular);
	font-size: 15px;
	font-weight: 400;
	line-height: 1.268000030517578em;
	color: ${colours.MIDNIGHT_BLUE};
	margin: 0;
	text-align: left;

	@media (min-width: ${device.laptop}) {
		display: block;
	}

	p {
		margin: 0;
	}
`;

const FaqIcon = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;

	/* Mobile: Chevron icon on the right, no background */
	&.mobile-icon {
		width: 30px;
		height: 25px;
		background-color: transparent;
	}

	/* Desktop: Contentful icon on the left, with background */
	&.desktop-icon {
		display: none;
		width: 50px;
		height: 50px;
		background-color: #f0f0f0;
		border-radius: 6px;

		@media (min-width: ${device.laptop}) {
			display: flex;
		}
	}

	@media (min-width: ${device.laptop}) {
		&.mobile-icon {
			display: none;
		}
	}
`;

function stripLeadingSlash(segment: string): string {
	return segment.startsWith("/") ? segment.slice(1) : segment;
}

/** Parent topic slug for an article when it appears under a main-page topic's articles list. */
function topicSlugForArticle(
	articleId: string,
	topics: IHelpsiteArticles[] | undefined,
): string | undefined {
	if (!topics?.length) return undefined;
	for (const topic of topics) {
		const articles = topic.fields.articles;
		if (!articles?.some((a) => a?.sys?.id === articleId)) continue;
		const url = topic.fields.topicsRelativeUrl;
		return url ? stripLeadingSlash(url) : undefined;
	}
	return undefined;
}

interface HelpFaqLinksProps {
	faqLinks: IHelpsiteArticle[];
	/** All topics (from getStaticProps): article id → topic slug segment */
	faqArticleTopicSlugById?: Record<string, string>;
	/** Fallback: homepage topics only (e.g. live preview if map is stale) */
	topics?: IHelpsiteArticles[] | undefined;
}

const HelpFaqLinks: FC<HelpFaqLinksProps> = ({
	faqLinks,
	faqArticleTopicSlugById = {},
	topics,
}) => {
	const router = useRouter();
	const pagePath = router.asPath.split("?")[0];

	return (
		<FaqList>
			{faqLinks.map((faqLink) => {
				const articleSlug = stripLeadingSlash(
					faqLink.fields.articleRelativeUrl || "",
				);
				const topicSlug =
					faqArticleTopicSlugById[faqLink.sys.id] ??
					topicSlugForArticle(faqLink.sys.id, topics);
				const faqUrl = topicSlug
					? `/help/${topicSlug}/${articleSlug}`
					: `/help/${articleSlug}`;

				return (
					<FaqLink
						key={faqLink.sys.id}
						href={faqUrl}
						onClick={() =>
							helpsiteTrackLink({
								helpsite_section: "faq_links",
								page_path: pagePath,
								link_href: faqUrl,
								label: faqLink.fields.articleMainHeader,
							})
						}
					>
						{/* Desktop: Icon on the left from Contentful */}
						{faqLink.fields.iconRef && (
							<FaqIcon className="desktop-icon">
								<Icon
									iconName={faqLink.fields.iconRef}
									iconSizes={{
										iconMobile: 28,
										iconTablet: 28,
										iconLaptop: 28,
										iconDesktop: 28,
										iconDesktopLarge: 28,
									}}
									alt={faqLink.fields.articleMainHeader}
								/>
							</FaqIcon>
						)}
						<FaqContentWrapper>
							<FaqTitle>{faqLink.fields.articleMainHeader}</FaqTitle>
							{/* Desktop only: Subcopy from content field
							{faqLink.fields.content &&
								faqLink.fields.content.nodeType &&
								faqLink.fields.content.content &&
								Array.isArray(faqLink.fields.content.content) && (
									<FaqSubcopy>
										{richTextHelper(faqLink.fields.content, richTextOptions)}
									</FaqSubcopy>
								)} */}
						</FaqContentWrapper>
						{/* Mobile: Chevron-right icon on the right (hardcoded) */}
						<FaqIcon className="mobile-icon">
							<Icon
								iconName="chevron-right"
								iconSizes={{
									iconMobile: 28,
									iconTablet: 28,
									iconLaptop: 28,
									iconDesktop: 28,
									iconDesktopLarge: 28,
								}}
								alt="Navigate"
							/>
						</FaqIcon>
					</FaqLink>
				);
			})}
		</FaqList>
	);
};

export default HelpFaqLinks;
